import React, { useMemo, useState } from "react";
import { MapPin } from "lucide-react";

// France ≈ l'Hexagone. Positions use the SVG viewBox 0 0 100 100.
// Rough coordinates chosen so the map reads correctly, not geo-accurate.
const CITIES = [
    { name: "Lille", x: 56, y: 15 },
    { name: "Paris", x: 50, y: 30 },
    { name: "Strasbourg", x: 82, y: 32 },
    { name: "Rennes", x: 22, y: 38 },
    { name: "Nantes", x: 24, y: 50 },
    { name: "Lyon", x: 64, y: 58 },
    { name: "Bordeaux", x: 28, y: 68 },
    { name: "Toulouse", x: 42, y: 82 },
    { name: "Marseille", x: 62, y: 85 },
    { name: "Nice", x: 78, y: 82 },
];

export default function FranceMap({ coaches = [], activeCity = "", onPick }) {
    const [hover, setHover] = useState(null);

    const byCity = useMemo(() => {
        const m = new Map();
        for (const c of coaches) {
            const key = (c.city || "").trim();
            if (!key) continue;
            m.set(key, (m.get(key) || 0) + 1);
        }
        return m;
    }, [coaches]);

    return (
        <section className="border-b border-white/10 bg-[#0f0f0f]">
            <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-8 px-6 py-16 lg:grid-cols-5">
                <div className="lg:col-span-2">
                    <div className="inline-flex items-center gap-2 border border-white/10 px-3 py-1.5">
                        <MapPin className="h-3 w-3 text-[#FF2E2E]" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">
                            L&apos;Hexagone
                        </span>
                    </div>
                    <h2 className="mt-4 font-display text-4xl uppercase leading-none tracking-tight text-white sm:text-5xl">
                        Un coach près de chez toi
                    </h2>
                    <p className="mt-4 max-w-md text-sm uppercase tracking-widest text-neutral-400">
                        Clique sur une ville pour filtrer l&apos;annuaire. Les points
                        rouges concentrent les coachs vérifiés. Les villes sans coach
                        sont marquées en creux.
                    </p>

                    <ul className="mt-6 grid grid-cols-2 gap-2 font-mono text-[10px] uppercase tracking-widest text-neutral-400">
                        {CITIES.map((c) => {
                            const count = byCity.get(c.name) || 0;
                            const active = activeCity === c.name;
                            return (
                                <li key={c.name}>
                                    <button
                                        data-testid={`france-city-${c.name}`}
                                        onClick={() => onPick && onPick(active ? "" : c.name)}
                                        className={`flex w-full items-center justify-between border px-2 py-1 transition-colors ${
                                            active
                                                ? "border-[#FF2E2E] bg-[#FF2E2E] text-white"
                                                : count
                                                ? "border-white/10 text-white hover:border-[#FF2E2E]"
                                                : "border-white/5 text-neutral-600"
                                        }`}
                                    >
                                        <span>{c.name}</span>
                                        <span
                                            className={
                                                active
                                                    ? "text-white"
                                                    : count
                                                    ? "text-[#FF2E2E]"
                                                    : "text-neutral-700"
                                            }
                                        >
                                            {count}
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                <div className="lg:col-span-3">
                    <div
                        data-testid="france-map"
                        className="relative aspect-square w-full border border-white/10 bg-[#0a0a0a] bg-grid"
                    >
                        <svg
                            viewBox="0 0 100 100"
                            preserveAspectRatio="xMidYMid meet"
                            className="h-full w-full"
                        >
                            {/* The Hexagon — France's informal shape */}
                            <polygon
                                points="50,4 93,27 93,73 50,96 7,73 7,27"
                                fill="transparent"
                                stroke="#262626"
                                strokeWidth="0.6"
                            />
                            {/* Decorative inner guides */}
                            <line x1="50" y1="4" x2="50" y2="96" stroke="#1a1a1a" strokeWidth="0.3" strokeDasharray="1,2" />
                            <line x1="7" y1="50" x2="93" y2="50" stroke="#1a1a1a" strokeWidth="0.3" strokeDasharray="1,2" />

                            {/* Compass */}
                            <g transform="translate(85 10)">
                                <text x="0" y="0" fill="#525252" fontFamily="monospace" fontSize="3" textAnchor="middle">N</text>
                                <line x1="0" y1="1" x2="0" y2="4" stroke="#525252" strokeWidth="0.3" />
                            </g>

                            {CITIES.map((c) => {
                                const count = byCity.get(c.name) || 0;
                                const active = activeCity === c.name;
                                const radius = count ? 1.6 + Math.min(count, 5) * 0.5 : 1.2;
                                return (
                                    <g
                                        key={c.name}
                                        onMouseEnter={() => setHover(c.name)}
                                        onMouseLeave={() => setHover(null)}
                                        onClick={() => onPick && onPick(active ? "" : c.name)}
                                        className="cursor-pointer"
                                    >
                                        {count > 0 && (
                                            <circle
                                                cx={c.x}
                                                cy={c.y}
                                                r={radius + 2.5}
                                                fill="#FF2E2E"
                                                opacity={active ? 0.35 : hover === c.name ? 0.2 : 0.08}
                                            >
                                                {!active && (
                                                    <animate
                                                        attributeName="r"
                                                        values={`${radius + 1.5};${radius + 3.5};${radius + 1.5}`}
                                                        dur="2.4s"
                                                        repeatCount="indefinite"
                                                    />
                                                )}
                                            </circle>
                                        )}
                                        <circle
                                            cx={c.x}
                                            cy={c.y}
                                            r={radius}
                                            fill={count ? (active ? "#ffffff" : "#FF2E2E") : "transparent"}
                                            stroke={count ? "transparent" : "#404040"}
                                            strokeWidth="0.4"
                                        />
                                        <text
                                            x={c.x}
                                            y={c.y - radius - 1.5}
                                            fill={active ? "#FF2E2E" : hover === c.name ? "#ffffff" : "#a3a3a3"}
                                            fontFamily="monospace"
                                            fontSize="2.6"
                                            textAnchor="middle"
                                            style={{ letterSpacing: "0.1em", pointerEvents: "none" }}
                                        >
                                            {c.name.toUpperCase()}
                                        </text>
                                    </g>
                                );
                            })}
                        </svg>

                        <div className="absolute bottom-3 left-3 border border-white/10 bg-black/70 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-400 backdrop-blur-sm">
                            {activeCity ? `Filtre : ${activeCity}` : "Clique pour filtrer"}
                        </div>
                        <div className="absolute bottom-3 right-3 flex items-center gap-3 border border-white/10 bg-black/70 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-400 backdrop-blur-sm">
                            <span className="inline-flex items-center gap-1">
                                <span className="h-1.5 w-1.5 bg-[#FF2E2E]" /> Coachs
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <span className="h-1.5 w-1.5 border border-neutral-600" /> Vide
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
